/**
 * @file     ViewingRequestSheet.tsx
 * @purpose  Request a viewing, one ListingSheet in place (design 5a to 5g):
 *           details (times, move-in date, name, email, mobile phone, the
 *           REALTOR® question, a note, the follow-up opt-in and SMS consent),
 *           an exact preview of what the recipient gets, then sent. Kind
 *           agnostic: the target picks the recipient line (the listing
 *           brokerage for an MLS® rental, Ray for an owner listing). Posts to
 *           /api/rentals/viewing-requests through secureFetch with the
 *           anti-spam fields. The draft is kept on this device until it sends.
 */
'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useListingEvents } from '@/hooks/useListingEvents';
import type { ListingSurface } from '@/core/analytics/listingEvents';
import { gatedSecureFetch } from '@/services/phone-gate/gatedFetch';
import { useAntiSpam } from '@/services/anti-spam/useAntiSpam';
import { AntiSpamHoneypot } from '@/components/forms/AntiSpamHoneypot';
import { ContactFields } from '@/components/lead-capture/ContactFields';
import { RealtorQuestion } from '@/components/lead-capture/RealtorQuestion';
import { ConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import { GatePrimaryButton, GateSecondaryButton, gateColors } from '@/components/gate/GateSheet';
import { ListingSheet, SheetError, SheetSent, type SheetListing } from '@/components/lead-capture/ListingSheet';
import { trackLeadConversion } from '@/services/analytics/leadConversion';
import { layoutTokens } from '@/design-system/tokens';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { RENTAL_PROFILE_PATH, VIEWING_REQUEST_LIMITS } from '@/core/config/viewingRequest';
import { pacificToday, formatAvailableDate } from '@/services/rentals/leaseListing';
import { useRentalsText } from './useRentalsText';
import { TimeSlotPicker } from './TimeSlotPicker';
import { FollowUpOptIn } from './FollowUpOptIn';
import {
  DRAFT_STORAGE_PREFIX,
  buildRequestBody,
  contactFromUser,
  emptyDraft,
  failureFor,
  hasErrors,
  slotDayLabel,
  suggestSlots,
  validateDraft,
  type SubmitFailure,
  type ViewingDraft,
  type ViewingErrors,
  type ViewingTarget,
} from './viewingRequestModel';

export interface ViewingSheetListing extends SheetListing {
  /** The address line the recipient sees in the subject. */
  address: string;
  mlsId: string | null;
  brokerageName: string | null;
  /** YYYY-MM-DD; a future date prefills the move-in field. */
  availableOn: string | null;
}

export interface ViewingRequestSheetProps {
  open: boolean;
  onClose: () => void;
  listing: ViewingSheetListing;
  target: ViewingTarget;
  surface: ListingSurface;
  /** The listing the events name (listing key, or the owner listing id). */
  analyticsListingKey: string;
  /** listing_source for analytics: the page the renter is on. */
  analyticsSource: 'mls' | 'owner';
  onSubmitted?: (result: { workingWithRealtor: boolean; followupOptIn: boolean }) => void;
  onSeeSimilar?: () => void;
}

type Step = 'details' | 'preview' | 'sent';
const NO_ERRORS: ViewingErrors = { contact: {}, realtor: false, note: false };

function readDraft(key: string): ViewingDraft | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ViewingDraft) : null;
  } catch {
    return null;
  }
}

function writeDraft(key: string, draft: ViewingDraft | null) {
  try {
    if (draft) window.sessionStorage.setItem(key, JSON.stringify(draft));
    else window.sessionStorage.removeItem(key);
  } catch {
    // Private mode or blocked storage: the draft lives in memory for this visit.
  }
}

function newSubmissionId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function ViewingRequestSheet({
  open,
  onClose,
  listing,
  target,
  surface,
  analyticsListingKey,
  analyticsSource,
  onSubmitted,
  onSeeSimilar,
}: ViewingRequestSheetProps) {
  const tr = useRentalsText();
  const { t } = useTranslation();
  const router = useRouter();
  const { user, isAuthenticated } = useAuth();
  const track = useListingEvents();
  const { setHoneypotValue, getAntiSpamMetadata } = useAntiSpam();
  const owner = target.recipient === 'owner';
  const storageKey = `${DRAFT_STORAGE_PREFIX}${target.listingKey ?? target.ownerListingId ?? 'unknown'}`;
  const today = useMemo(() => pacificToday(), []);
  const suggestions = useMemo(() => suggestSlots(today), [today]);
  const defaultMoveIn = listing.availableOn && listing.availableOn > today ? listing.availableOn : '';

  const [step, setStep] = useState<Step>('details');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [draft, setDraft] = useState<ViewingDraft>(() => emptyDraft(contactFromUser(null), defaultMoveIn));
  const [errors, setErrors] = useState<ViewingErrors>(NO_ERRORS);
  const [failure, setFailure] = useState<SubmitFailure | null>(null);
  const [pending, setPending] = useState(false);
  const [brokerage, setBrokerage] = useState<string | null>(listing.brokerageName);
  const submissionIdRef = useRef<string>(newSubmissionId());
  const sentRef = useRef(false);
  const kind = { listing_type: 'lease' as const, listing_source: analyticsSource };

  useEffect(() => {
    if (!open) return;
    sentRef.current = false;
    submissionIdRef.current = newSubmissionId();
    setStep('details');
    setDirection('forward');
    setErrors(NO_ERRORS);
    setFailure(null);
    setBrokerage(listing.brokerageName);
    const saved = readDraft(storageKey);
    const base = emptyDraft(contactFromUser(isAuthenticated ? user : null), defaultMoveIn);
    setDraft(saved ? { ...base, ...saved, contact: { ...base.contact, ...pickFilled(saved.contact) } } : base);
    track('viewing_request_opened', { surface, listing_key: analyticsListingKey, cta_id: 'request_viewing', ...kind });

    if (!isAuthenticated) return;
    const ctrl = new AbortController();
    // The rental profile prefills the move-in date when the renter has one. Absent route or profile: nothing to fill.
    fetch('/api/rentals/profile', { signal: ctrl.signal, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { profile?: { moveIn?: string | null; move_in?: string | null } | null } | null) => {
        const moveIn = body?.profile?.moveIn ?? body?.profile?.move_in ?? null;
        if (!ctrl.signal.aborted && moveIn && /^\d{4}-\d{2}-\d{2}$/.test(moveIn)) {
          setDraft((d) => (d.moveIn && d.moveIn !== defaultMoveIn ? d : { ...d, moveIn }));
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
    // Once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && step !== 'sent') writeDraft(storageKey, draft);
  }, [draft, open, step, storageKey]);

  const update = useCallback(<K extends keyof ViewingDraft>(key: K, value: ViewingDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFailure(null);
  }, []);

  const close = () => {
    if (pending) return;
    if (step !== 'sent' && !sentRef.current) {
      track('viewing_request_abandoned', { surface, listing_key: analyticsListingKey, step: step === 'preview' ? 'preview' : 'details', ...kind });
    }
    onClose();
  };

  const go = (next: Step, dir: 'forward' | 'back') => {
    setDirection(dir);
    setStep(next);
  };

  const toPreview = () => {
    const found = validateDraft(draft, t);
    setErrors(found);
    if (hasErrors(found)) return;
    track('viewing_request_previewed', { surface, listing_key: analyticsListingKey, times: draft.slots.length, ...kind });
    go('preview', 'forward');
  };

  const send = async () => {
    if (pending) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setFailure('offline');
      return;
    }
    setFailure(null);
    setPending(true);
    try {
      const body = buildRequestBody(draft, target, {
        antiSpam: getAntiSpamMetadata(),
        submissionId: submissionIdRef.current,
        sourcePage: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
      const res = await gatedSecureFetch('/api/rentals/viewing-requests', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setFailure(failureFor(res.status, true));
        return;
      }
      const data = (await res.json().catch(() => null)) as { requestId?: string | null; recipient?: { brokerageName?: string | null } } | null;
      if (data?.recipient?.brokerageName) setBrokerage(data.recipient.brokerageName);
      sentRef.current = true;
      writeDraft(storageKey, null);
      track('viewing_request_submitted', {
        surface,
        listing_key: analyticsListingKey,
        realtor: draft.realtor ? 'yes' : 'no',
        followup_optin: draft.followupOptIn,
        sms_optin: draft.smsOptIn,
        ...kind,
      });
      trackLeadConversion(owner ? 'owner_listing' : 'tenant', {
        flowId: 'viewingRequest',
        sourcePage: body.sourcePage,
        referenceId: `viewing-${data?.requestId ?? submissionIdRef.current}`,
      });
      onSubmitted?.({ workingWithRealtor: draft.realtor === true, followupOptIn: draft.followupOptIn });
      go('sent', 'forward');
    } catch {
      setFailure(failureFor(null, typeof navigator === 'undefined' || navigator.onLine !== false));
    } finally {
      setPending(false);
    }
  };

  const title =
    step === 'details' ? tr('viewing.title') : step === 'preview' ? tr('viewing.previewTitle') : tr(owner ? 'viewing.sentTitleOwner' : 'viewing.sentTitle');
  const lead = step === 'details' ? tr(owner ? 'ownerLine' : 'forwardLine') : step === 'preview' ? tr(owner ? 'viewing.previewLeadOwner' : 'viewing.previewLead') : undefined;
  const failureText =
    failure === 'offline' ? tr('viewing.offline')
    : failure === 'unavailable' ? tr('viewing.unavailable')
    : failure === 'rate_limited' ? tr('viewing.tooMany')
    : failure ? tr('viewing.failed')
    : null;

  return (
    <ListingSheet
      open={open}
      onClose={close}
      listing={listing}
      title={title}
      titleHidden={step === 'sent'}
      lead={lead}
      steps={step === 'sent' ? undefined : { current: step === 'details' ? 1 : 2, total: 2 }}
      onBack={step === 'preview' ? () => go('details', 'back') : undefined}
      stepKey={step}
      stepDirection={direction}
      testId="viewing-request-sheet"
    >
      {step === 'details' ? (
        <>
          <AntiSpamHoneypot onValueChange={setHoneypotValue} />
          <TimeSlotPicker
            value={draft.slots}
            onChange={(slots) => update('slots', slots)}
            suggestions={suggestions}
            today={today}
            recipient={target.recipient}
            disabled={pending}
            idPrefix="viewing"
          />
          <TextField
            id="viewing-move-in"
            name="moveIn"
            type="date"
            label={tr('viewing.moveIn')}
            value={draft.moveIn}
            onChange={(e) => update('moveIn', e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
            fullWidth
          />
          <ContactFields
            values={draft.contact}
            errors={errors.contact}
            onChange={(field, value) => {
              setDraft((d) => ({ ...d, contact: { ...d.contact, [field]: value } }));
              setErrors((e) => ({ ...e, contact: { ...e.contact, [field]: undefined } }));
            }}
            disabled={pending}
            idPrefix="viewing"
          />
          <RealtorQuestion
            value={draft.realtor}
            onChange={(v) => {
              update('realtor', v);
              setErrors((e) => ({ ...e, realtor: false }));
            }}
            yesLine={tr('viewing.realtorYes')}
            error={errors.realtor}
            disabled={pending}
            idPrefix="viewing"
          />
          <TextField
            id="viewing-note"
            name="note"
            label={tr('viewing.note')}
            placeholder={tr('viewing.notePlaceholder')}
            value={draft.note}
            onChange={(e) => update('note', e.target.value)}
            multiline
            minRows={2}
            fullWidth
            error={errors.note}
            slotProps={{ htmlInput: { maxLength: VIEWING_REQUEST_LIMITS.noteMax } }}
          />
          <FollowUpOptIn checked={draft.followupOptIn} onChange={(v) => update('followupOptIn', v)} recipient={target.recipient} disabled={pending} />
          <ConsentChoices
            phone
            values={{ email: draft.emailOptIn, sms: draft.smsOptIn }}
            onChange={(v) => setDraft((d) => ({ ...d, emailOptIn: v.email, smsOptIn: v.sms }))}
            disabled={pending}
          />
          {failureText ? <SheetError>{failureText}</SheetError> : null}
          <GatePrimaryButton onClick={toPreview}>{tr('viewing.previewCta')}</GatePrimaryButton>
          <Typography component="p" sx={{ m: 0, fontSize: 12.5, lineHeight: 1.5, color: gateColors.muted }}>
            {tr('viewing.draftKept')}
          </Typography>
        </>
      ) : step === 'preview' ? (
        <>
          <PreviewCard draft={draft} listing={listing} owner={owner} brokerage={brokerage} />
          {draft.followupOptIn && !owner ? (
            <Typography component="p" data-kept-note sx={{ m: 0, fontSize: 13.5, lineHeight: 1.5, color: gateColors.textSecondary }}>
              {tr('viewing.keptNote')}
            </Typography>
          ) : null}
          {failureText ? <SheetError>{failureText}</SheetError> : null}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <GateSecondaryButton onClick={() => go('details', 'back')} disabled={pending}>
              {tr('viewing.edit')}
            </GateSecondaryButton>
            <GatePrimaryButton pending={pending} onClick={() => void send()}>
              {tr('viewing.send')}
            </GatePrimaryButton>
          </Box>
          <FormConsentLine action={t('forms.consent.sending', { defaultValue: 'sending' })} />
        </>
      ) : (
        <SheetSent
          title={title}
          line={
            owner
              ? tr('viewing.sentLineOwner')
              : brokerage
                ? tr('viewing.sentLine', { brokerage: brokerage.replace(/\.+$/, '') })
                : tr('viewing.sentLineNoBrokerage')
          }
          primary={{
            label: tr('viewing.startProfile'),
            onClick: () => {
              onClose();
              router.push(RENTAL_PROFILE_PATH);
            },
          }}
          secondary={
            onSeeSimilar
              ? {
                  label: tr('viewing.seeSimilar'),
                  onClick: () => {
                    onClose();
                    onSeeSimilar();
                  },
                }
              : { label: tr('viewing.done'), onClick: onClose }
          }
        >
          <SentSteps owner={owner} />
          <Typography component="p" sx={{ m: 0, mt: 2, fontSize: 14, fontWeight: fontWeightTokens.medium, color: gateColors.text }}>
            {tr('viewing.fasterTitle')}
          </Typography>
          <ButtonBase
            component="a"
            href={`${RENTAL_PROFILE_PATH}#lease-end`}
            sx={{
              minHeight: 44,
              px: 2,
              borderRadius: `${layoutTokens.radius.sm}px`,
              fontFamily: 'inherit',
              fontSize: 14.5,
              fontWeight: fontWeightTokens.medium,
              color: gateColors.link,
              '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2 },
            }}
          >
            {tr('viewing.leaseReminder')}
          </ButtonBase>
        </SheetSent>
      )}
    </ListingSheet>
  );
}

function pickFilled(contact: ViewingDraft['contact']): Partial<ViewingDraft['contact']> {
  return Object.fromEntries(Object.entries(contact ?? {}).filter(([, v]) => typeof v === 'string' && v.trim() !== ''));
}

function SentSteps({ owner }: { owner: boolean }) {
  const tr = useRentalsText();
  const steps = owner ? [tr('viewing.stepOwner1'), tr('viewing.stepOwner2'), tr('viewing.step3')] : [tr('viewing.step1'), tr('viewing.step2'), tr('viewing.step3')];
  return (
    <Box component="ol" aria-label={tr('viewing.stepsLabel')} sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'flex', flexDirection: 'column', gap: 2.5, textAlign: 'left', width: '100%' }}>
      {steps.map((s, i) => (
        <Box component="li" key={s} sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 24,
              height: 24,
              borderRadius: `${layoutTokens.radius.pill}px`,
              backgroundColor: gateColors.chip,
              color: gateColors.chipText,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12.5,
              fontWeight: fontWeightTokens.semibold,
            }}
          >
            {i + 1}
          </Box>
          <Typography component="span" sx={{ fontSize: 14.5, lineHeight: 1.5, color: gateColors.textSecondary }}>
            {s}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function PreviewCard({ draft, listing, owner, brokerage }: { draft: ViewingDraft; listing: ViewingSheetListing; owner: boolean; brokerage: string | null }) {
  const tr = useRentalsText();
  const to = owner ? tr('viewing.toOwner') : brokerage ? tr('viewing.to', { brokerage }) : tr('viewing.toNoBrokerage');
  const subject = listing.mlsId && !owner ? tr('viewing.subjectMls', { address: listing.address, id: listing.mlsId }) : tr('viewing.subject', { address: listing.address });
  const times = draft.slots.length
    ? draft.slots.map((s) => `${slotDayLabel(s.date)}, ${tr(`viewing.windowLower.${s.window}`)}`).join(' · ')
    : tr('viewing.anyTime');
  const rows: Array<[string, string]> = [
    [tr('viewing.row.name'), `${draft.contact.firstName.trim()} ${draft.contact.lastName.trim()}`.trim()],
    [tr('viewing.row.email'), draft.contact.email.trim()],
    [tr('viewing.row.phone'), draft.contact.phone.trim()],
    [tr('viewing.row.times'), times],
    [tr('viewing.row.moveIn'), draft.moveIn ? formatAvailableDate(draft.moveIn) : tr('viewing.notGiven')],
    [tr('viewing.row.realtor'), draft.realtor ? tr('terms.yes') : tr('terms.no')],
    ...(draft.note.trim() ? [[tr('viewing.row.note'), draft.note.trim()] as [string, string]] : []),
  ];
  return (
    <Box data-viewing-preview sx={{ borderRadius: `${layoutTokens.radius.md}px`, border: `1px solid ${gateColors.border}`, backgroundColor: gateColors.surface, overflow: 'hidden' }}>
      <Box sx={{ px: 4, py: 3, backgroundColor: gateColors.surfaceSunken, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography component="p" sx={{ m: 0, fontSize: 13.5, color: gateColors.textSecondary }}>{to}</Typography>
        <Typography component="p" sx={{ m: 0, fontSize: 13.5, fontWeight: fontWeightTokens.medium, color: gateColors.text, overflowWrap: 'anywhere' }}>{subject}</Typography>
      </Box>
      <Box component="dl" sx={{ m: 0, px: 4, py: 1 }}>
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', gap: 3, py: 2, borderTop: `1px solid ${gateColors.border}`, '&:first-of-type': { borderTop: 'none' } }}>
            <Box component="dt" sx={{ flex: '0 0 38%', fontSize: 13.5, color: gateColors.muted }}>{label}</Box>
            <Box component="dd" sx={{ m: 0, flex: 1, minWidth: 0, fontSize: 14, color: gateColors.text, overflowWrap: 'anywhere' }}>{value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
